/**
 * De filmlook: belichting, tone mapping, gradatie en lensfouten in één pass.
 *
 * ## Waarom dit bestand er is
 *
 * De scène werd tot nu toe zonder tone mapping getoond. Dat stond niet zo in
 * de code — `CANVAS_INSTELLINGEN` zet netjes `ACESFilmicToneMapping` — maar de
 * `EffectComposer` van @react-three/postprocessing zet `gl.toneMapping` bij het
 * monteren op `NoToneMapping`, en er stond geen `ToneMapping`-effect in de
 * keten. Elk licht boven 1,0 werd dus hard afgekapt: een armatuur was een witte
 * vlek met een rand, een lichtbundel een driehoek. Juist die harde afkapping is
 * waar het oog "computerbeeld" aan herkent. Film heeft een schouder: licht
 * loopt zacht dicht naar wit en verliest daarbij verzadiging.
 *
 * ## Wat er in zit, in volgorde
 *
 *  1. **Chromatische aberratie** — radiaal, en in de lineaire HDR-buffer, dus
 *     vóór de tone mapping. Zo gedraagt een lens zich: het rood en blauw van een
 *     fel armatuur schuiven uiteen, een donker vlak merkt er niets van.
 *  2. **Belichting** — een lineaire factor, vóór de curve, zoals een diafragma.
 *  3. **AgX** — de tone mapper van Blender 4 en three r160+. Gekozen boven ACES
 *     om het merkrood: ACES draait verzadigd rood in de hooglichten naar oranje,
 *     AgX houdt de tint en laat hem alleen naar wit lopen.
 *  4. **Look** — contrast en verzadiging in de log-ruimte van AgX ("punchy").
 *  5. **Merkrood** — alle kleur behalve rood gaat er deels uit: de look van
 *     "From warehouse to powerhouse" (Linde MH), een koele hal waarin alleen
 *     het rood spreekt.
 *  6. **Split toning** — koele schaduwen, en sinds 22 oktober ook koele
 *     hooglichten. Bescheiden, omdat het merkrood zijn tint moet houden.
 *  7. **Vignettering** — cos⁴-achtige lensafval, niet een zwarte ring.
 *  8. **Filmkorrel** — in beeldruimte, sterkst in de middentonen, per frame
 *     anders. Een volstrekt schoon beeld leest als een render; korrel geeft het
 *     oog iets om op te focussen en maskeert banding in de mist.
 *
 * Alles in één pass, omdat elke extra volschermpass op de beurs-pc een
 * volledige lees- en schrijfronde van 8 MB (1080p, half float) kost.
 */
import { Effect, EffectAttribute, BlendFunction } from 'postprocessing'
import * as THREE from 'three'

const fragment = /* glsl */ `
uniform float belichting;
uniform float aberratie;
uniform float lookContrast;
uniform float lookVerzadiging;
uniform vec3 schaduwTint;
uniform vec3 lichtTint;
uniform float tintSterkte;
uniform float merkrood;
uniform float vignet;
uniform float korrel;
uniform float korrelTijd;

// AgX, overgenomen uit three r169 (tonemapping_pars_fragment), met een eigen
// belichtingsuniform: de renderer-exposure staat door de composer op 1.
const mat3 SRGB_NAAR_2020 = mat3(
  vec3(0.6274, 0.0691, 0.0164),
  vec3(0.3293, 0.9195, 0.0880),
  vec3(0.0433, 0.0113, 0.8956)
);
const mat3 R2020_NAAR_SRGB = mat3(
  vec3(1.6605, -0.1246, -0.0182),
  vec3(-0.5876, 1.1329, -0.1006),
  vec3(-0.0728, -0.0083, 1.1187)
);

vec3 agxContrast(vec3 x) {
  vec3 x2 = x * x;
  vec3 x4 = x2 * x2;
  return 15.5 * x4 * x2 - 40.14 * x4 * x + 31.96 * x4 - 6.868 * x2 * x + 0.4298 * x2 + 0.1191 * x - 0.00232;
}

vec3 agx(vec3 kleur) {
  const mat3 inset = mat3(
    vec3(0.856627153315983, 0.137318972929847, 0.11189821299995),
    vec3(0.0951212405381588, 0.761241990602591, 0.0767994186031903),
    vec3(0.0482516061458583, 0.101439036467562, 0.811302368396859)
  );
  const mat3 outset = mat3(
    vec3(1.1271005818144368, -0.1413297634984383, -0.14132976349843826),
    vec3(-0.11060664309660323, 1.157823702216272, -0.11060664309660294),
    vec3(-0.016493938717834573, -0.016493938717834257, 1.2519364065950405)
  );
  const float minEv = -12.47393;
  const float maxEv = 4.026069;

  kleur = SRGB_NAAR_2020 * kleur;
  kleur = inset * kleur;
  kleur = max(kleur, 1e-10);
  kleur = log2(kleur);
  kleur = (kleur - minEv) / (maxEv - minEv);
  kleur = clamp(kleur, 0.0, 1.0);
  kleur = agxContrast(kleur);

  // De look, in de weergaveruimte van AgX — hier en niet na de curve, anders
  // knipt het contrast de hooglichten die AgX net zacht had laten lopen.
  float l = dot(kleur, vec3(0.2126, 0.7152, 0.0722));
  kleur = pow(max(kleur, 0.0), vec3(lookContrast));
  kleur = l + lookVerzadiging * (kleur - l);

  kleur = outset * kleur;
  kleur = pow(max(vec3(0.0), kleur), vec3(2.2));
  kleur = R2020_NAAR_SRGB * kleur;
  return clamp(kleur, 0.0, 1.0);
}

// Goedkope, stabiele hash — geen textuur nodig voor de korrel.
float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  vec2 c = uv - 0.5;
  // Beeldverhouding meenemen, anders is de aberratie en het vignet ovaal.
  vec2 ca = vec2(c.x * aspect, c.y);
  float r2 = dot(ca, ca);

  // 1. Chromatische aberratie: nul in het midden, kwadratisch naar de rand.
  vec3 hdr;
  if (aberratie > 0.0) {
    vec2 verschuif = c * r2 * aberratie;
    hdr.r = texture2D(inputBuffer, uv - verschuif).r;
    hdr.g = inputColor.g;
    hdr.b = texture2D(inputBuffer, uv + verschuif).b;
  } else {
    hdr = inputColor.rgb;
  }

  // 2 + 3 + 4. Belichting, AgX en de look.
  vec3 ldr = agx(hdr * belichting);

  vec3 d = pow(ldr, vec3(1.0 / 2.2));
  float luma = dot(d, vec3(0.2126, 0.7152, 0.0722));

  // 5. Merkrood. Rood is hier: rood duidelijk boven groen en blauw, en groen
  // en blauw ongeveer gelijk — de lak van een Linde, de liggers, de
  // lichtlijnen. Karton en hout (rood boven groen boven blauw) tellen niet
  // mee, het geel van de aanrijdbeveiliging ook niet. Gemeten als verhouding
  // tot het rood zelf, zodat een rode ligger in de schaduw net zo rood blijft
  // als een truck onder een armatuur.
  float rmax = max(d.r, 1e-3);
  float roodheid = smoothstep(0.35, 0.7, (d.r - max(d.g, d.b)) / rmax)
                 * (1.0 - smoothstep(0.12, 0.3, abs(d.g - d.b) / rmax));
  d = luma + (d - luma) * mix(1.0 - merkrood, 1.0, roodheid);

  // 6. Split toning in weergaveruimte (ongeveer gamma 2.2).
  float schaduwGewicht = (1.0 - smoothstep(0.0, 0.55, luma)) * tintSterkte;
  float lichtGewicht = smoothstep(0.35, 1.0, luma) * tintSterkte;
  // Een tint als verhouding tot zijn eigen helderheid: zo verschuift hij de
  // kleur zonder het beeld lichter of donkerder te maken, welke hex er ook in
  // de Studio gekozen wordt. De kleine optelling tilt het zwart iets op in de
  // schaduwtint — de "gelifte zwarten" van een filmscan, in plaats van dood 0.
  vec3 w = vec3(0.2126, 0.7152, 0.0722);
  vec3 sT = schaduwTint / max(dot(schaduwTint, w), 1e-4);
  vec3 lT = lichtTint / max(dot(lichtTint, w), 1e-4);
  d = mix(d, d * sT, schaduwGewicht) + (sT - 1.0) * 0.018 * schaduwGewicht;
  d = mix(d, d * lT, lichtGewicht);

  // 7. Vignettering: zacht, en nooit helemaal zwart in de hoeken.
  float v = 1.0 - smoothstep(0.18, 1.05, sqrt(r2) * 1.18);
  d *= mix(1.0, v, vignet);

  // 8. Filmkorrel. Twee hashes per pixel geven een iets grovere, minder digitale
  // korrel dan één. Middentonen krijgen de meeste, zwart en wit het minst.
  vec2 px = uv * resolution;
  float n = hash12(px + korrelTijd * 91.7) + hash12(px * 0.5 + korrelTijd * 57.3) - 1.0;
  float gewicht = 1.0 - pow(abs(luma * 2.0 - 1.0), 2.0) * 0.7;
  d += n * korrel * gewicht;

  outputColor = vec4(pow(max(d, 0.0), vec3(2.2)), inputColor.a);
}
`

export interface FilmLookOpties {
  belichting?: number
  aberratie?: number
  lookContrast?: number
  lookVerzadiging?: number
  schaduwTint?: THREE.ColorRepresentation
  lichtTint?: THREE.ColorRepresentation
  tintSterkte?: number
  merkrood?: number
  vignet?: number
  korrel?: number
}

export class FilmLookEffect extends Effect {
  private tijd = 0

  constructor({
    belichting = 1,
    aberratie = 0.004,
    lookContrast = 1.25,
    lookVerzadiging = 1.15,
    schaduwTint = '#3f5866',
    lichtTint = '#8a6f52',
    tintSterkte = 0.35,
    merkrood = 0,
    vignet = 0.5,
    korrel = 0.035,
  }: FilmLookOpties = {}) {
    super('FilmLookEffect', fragment, {
      // CONVOLUTION: de aberratie leest buurpixels uit `inputBuffer`. Dat maakt
      // dit een eigen pass, en dat is precies wat we willen: de tone mapping
      // hoort na bloom en scherptediepte te komen, niet ermee versmolten.
      attributes: EffectAttribute.CONVOLUTION,
      blendFunction: BlendFunction.SET,
      uniforms: new Map<string, THREE.Uniform>([
        ['belichting', new THREE.Uniform(belichting)],
        ['aberratie', new THREE.Uniform(aberratie)],
        ['lookContrast', new THREE.Uniform(lookContrast)],
        ['lookVerzadiging', new THREE.Uniform(lookVerzadiging)],
        ['schaduwTint', new THREE.Uniform(new THREE.Color(schaduwTint))],
        ['lichtTint', new THREE.Uniform(new THREE.Color(lichtTint))],
        ['tintSterkte', new THREE.Uniform(tintSterkte)],
        ['merkrood', new THREE.Uniform(merkrood)],
        ['vignet', new THREE.Uniform(vignet)],
        ['korrel', new THREE.Uniform(korrel)],
        ['korrelTijd', new THREE.Uniform(0)],
      ]),
    })
  }

  /** Per frame een nieuwe korrel. Stilstaande korrel leest als vuil op de lens. */
  update(_r: THREE.WebGLRenderer, _i: THREE.WebGLRenderTarget, delta?: number) {
    const u = this.uniforms.get('korrelTijd')!
    // Afgerond op 24 stappen per seconde: korrel die met 60 fps ververst, is
    // te fijn om als film te lezen en ziet eruit als videoruis.
    this.tijd += delta ?? 0.016
    u.value = Math.floor(this.tijd * 24) % 1000
  }

  zet(sleutel: keyof FilmLookOpties, waarde: number | string) {
    const u = this.uniforms.get(sleutel)
    if (!u) return
    if (u.value instanceof THREE.Color) u.value.set(waarde as THREE.ColorRepresentation)
    else u.value = waarde
  }
}
